import { useContext, useState, Fragment, useRef, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MobXProviderContext } from "mobx-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { preventMinus, preventPasteNegative } from "utils/helpers/validation";
import {
  useMealNextAvailableDate,
  getValidMealList,
} from "utils/hooks/useMealNextAvailableDate";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { Calendar } from "react-multi-date-picker";
import dayjs from "dayjs";
import object from "utils/helpers/object";
import classNames from "classnames";

function ChangeExpectedOrdesMealPlan({
  visible,
  setModal,
  meals,
  expectedOrders,
  calculationMode,
  parsed_expected_bags,
}) {
  const [expectedBags, changeExpectedBags] = useState("");
  const [warning, setWarning] = useState(false);
  const [disabledDays, setDisabledDays] = useState({});
  const { mealManagementStore } = useContext(MobXProviderContext);
  const { userStore } = useContext(MobXProviderContext);
  const calendarRef = useRef(null);
  const nextAvailableMeal = useMealNextAvailableDate(meals);
  const validMeals = getValidMealList(meals);
  const [changeEffect, setChangeEffect] = useState("next");
  const [selectedCustomDate, setSelectedCustomDate] = useState(null);
  const [errors, setErrors] = useState({});
  const mealPlan = mealManagementStore.mealPlan;
  const { locationID } = userStore;

  useEffect(() => {
    const source =
      calculationMode == "same_day_average"
        ? parsed_expected_bags
        : expectedOrders;

    changeExpectedBags(source);

    if (typeof source === "object") {
      const initialDisabled = {};
      for (const [day, value] of Object.entries(source)) {
        if (Number(value) === 0) {
          initialDisabled[day] = true;
        }
      }
      setDisabledDays(initialDisabled);
    }
  }, [visible]);

  const onSavePress = () => {
    const isObject = typeof expectedBags === "object";
    let validationErrors = {};

    if (isObject) {
      Object.entries(expectedBags).forEach(([day, value]) => {
        if (
          !disabledDays[day] &&
          (value === "" || value === null || isNaN(value))
        ) {
          validationErrors[day] = "Required";
        }
      });
    } else {
      if (!expectedBags || expectedBags.trim() === "") {
        validationErrors["global"] = "Expected order is required.";
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    } else {
      setErrors({});
    }

    if (!warning && object.isPresent(nextAvailableMeal)) {
      setWarning(true);
      return;
    }

    let formattedBags = expectedBags;

    if (isObject) {
      formattedBags = Object.entries(expectedBags)
        .filter(
          ([_, v]) => v !== "" && v !== null && !isNaN(v) && Number(v) !== 0
        )
        .map(([day, value]) => `${day}:${Number(value)}`)
        .join(",");
    }

    const payload = {
      meal_series: {
        expected_bags: formattedBags,
      },
    };

    if (object.isPresent(nextAvailableMeal)) {
      payload.meal_series.effective_date =
        changeEffect === "custom"
          ? selectedCustomDate ?? nextAvailableMeal?.mealDate
          : nextAvailableMeal?.mealDate;
    }

    mealManagementStore
      .updateMealPlan(locationID, mealPlan?.id, payload)
      .then(() => setModal(false));
  };

  const handleDateChange = (value) => {
    const selectedDate = dayjs(value.toDate()).format("YYYY-MM-DD");

    setSelectedCustomDate(selectedDate);
  };

  const dayOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const dayAbbr = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
  };

  const renderExpectedBags = (bags) => {
    if (!bags) return null;
    if (typeof bags === "string") {
      return <div>{bags}</div>;
    }

    return (
      <div className="">
        {dayOrder
          ?.filter((day) => Number(bags[day]) > 0)
          ?.map((day) => (
            <div
              key={day}
              className="text-dark-gray font-inter-regular text-[14px]"
            >
              <span className="font-inter-regular text-dark-gray text-[14px] inline-block w-[40px]">
                {dayAbbr[day]}:
              </span>{" "}
              {bags[day] ?? 0}
            </div>
          ))}
      </div>
    );
  };

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-[90]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center sm:p-4 md:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {warning ? (
                <Dialog.Panel className="w-[696px] min-h-[276px] d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all overflow-visible">
                  <div className="d-col">
                    <div className="flex justify-between items-center mb-[22px]">
                      <button
                        className="font-inter-regular text-[16px] text-hh-accent-dark"
                        onClick={() => {
                          setWarning(false);
                          setSelectedCustomDate(null);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          size="xs"
                          className="mr-2"
                          style={{
                            color: "#130f26",
                          }}
                        />
                        Back
                      </button>

                      <button
                        type="button"
                        onClick={() => setModal(false)}
                        className="btn-style"
                      >
                        <FontAwesomeIcon
                          style={{ width: 16, height: 26 }}
                          icon={faXmark}
                          size="2x"
                          color="#BBC0C5"
                        />
                      </button>
                    </div>

                    <h2 className="text-2xl font-inter-bold text-dark-gray">
                      Set Date for Changes
                    </h2>

                    <small className="font-inter-regular text-dark-gray text-[16px] mt-[16px] bg-light-gray py-[12px] px-[8px] rounded-lg">
                      Expected orders change requested to{" "}
                      {renderExpectedBags(expectedBags)} These changes will
                      apply to all future meals in the series.
                    </small>

                    <strong className="text-dark-gray font-inter-semibold text-[16px] my-[16px]">
                      When should they take effect?
                    </strong>

                    <div className="d-col rounded-lg">
                      <div key="next" className="flex items-center">
                        <input
                          name="date-method"
                          type="radio"
                          value="next"
                          checked={changeEffect == "next"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="photo"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Next available meal in series:{" "}
                          {nextAvailableMeal?.mealDate}
                        </label>
                      </div>

                      <div key="custom" className="flex items-center mt-[16px]">
                        <input
                          name="date-method"
                          type="radio"
                          value={"custom"}
                          checked={changeEffect == "custom"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="signature"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Pick a custom date
                        </label>
                      </div>
                    </div>

                    {changeEffect == "custom" && (
                      <div
                        className="flex flex-row justify-center items-center mt-[16px]"
                        ref={calendarRef}
                      >
                        <Calendar
                          highlightToday={true}
                          className="shadow-lg"
                          isMulti={false}
                          onChange={handleDateChange}
                          disableYearPicker={true}
                          monthYearSeparator={" "}
                          mapDays={({ date }) => {
                            const allowedDates = validMeals?.map(
                              (meal) => meal?.meal_date
                            );
                            const formatted = dayjs(date.toDate()).format(
                              "YYYY-MM-DD"
                            );
                            const isEnabled = allowedDates.includes(formatted);

                            return {
                              disabled: !isEnabled,
                              style: isEnabled
                                ? { color: "#000" }
                                : {
                                    color: "#ccc",
                                    textDecoration: "",
                                    pointerEvents: "none",
                                  },
                            };
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center items-center mt-5">
                    <button
                      className="btn-outline btn-style w-full"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className={classNames({
                        "btn-purple btn-style px-6 ml-3 w-full": true,
                        "btn-disabled":
                          changeEffect === "custom" && !selectedCustomDate,
                      })}
                      onClick={onSavePress}
                      disabled={changeEffect == "custom" && !selectedCustomDate}
                    >
                      Request changes
                    </button>
                  </div>
                </Dialog.Panel>
              ) : (
                <Dialog.Panel className="w-[696px] min-h-[276px] d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all">
                  <div className="d-row justify-between items-center">
                    <h2 className="text-2xl font-inter-bold">Expected order</h2>
                    <button
                      onClick={() => setModal(false)}
                      className="text-dark-gray hover:text-yellow-600"
                      aria-label="Close"
                    >
                      <span className="text-xl">&times;</span>
                    </button>
                  </div>

                  <span className="font-inter-regular my-[16px] text-secondary text-[16px]">
                    These changes will apply to all future meals in the series
                    and not for a single specific day.
                  </span>

                  <span className="font-inter-regular text-dark-gray text-[14px]">
                    The number of people expected to place an order
                  </span>

                  {typeof expectedBags === "object" ? (
                    <div className="grid grid-cols-1 gap-4 mt-2">
                      {Object.keys(expectedBags).map((day) => {
                        const value = expectedBags[day];
                        const isDisabled = value === 0 && value !== "";

                        return (
                          <div key={day} className="flex flex-row items-center">
                            <label
                              className={classNames({
                                "capitalize text-sm w-1/3 font-inter-regular mb-1": true,
                                "text-[#DEE2E6]": isDisabled,
                                "text-dark-gray": !isDisabled,
                              })}
                            >
                              {day}
                            </label>

                            <input
                              className={`border ${
                                errors[day]
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } text-dark-gray w-3/4 focus:outline-none rounded-md text-md py-[9px] px-[12px] ${
                                isDisabled
                                  ? "bg-[#DEE2E6] cursor-not-allowed"
                                  : ""
                              }`}
                              type="number"
                              min="0"
                              disabled={isDisabled}
                              value={isDisabled ? "" : value}
                              onKeyDown={preventMinus}
                              onPaste={preventPasteNegative}
                              onWheelCapture={(e) => e.target.blur()}
                              onChange={(e) =>
                                changeExpectedBags({
                                  ...expectedBags,
                                  [day]: e.target.value,
                                })
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <input
                        className={`flex w-full bg-background text-dark-gray focus:outline-none rounded-md text-md py-[9px] p-[19px] ${
                          errors.global ? "border border-red-500" : ""
                        }`}
                        value={expectedBags}
                        type="number"
                        min="0"
                        onKeyDown={preventMinus}
                        onPaste={preventPasteNegative}
                        onWheelCapture={(e) => {
                          e.target.blur();
                        }}
                        onChange={(e) => changeExpectedBags(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="d-row w-full items-center mt-[24px] justify-center">
                    <button
                      className="btn-light w-full btn-style py-3 px-5"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn-purple w-full btn-style py-3 px-6 ml-3"
                      onClick={onSavePress}
                    >
                      Request
                    </button>
                  </div>
                </Dialog.Panel>
              )}
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default ChangeExpectedOrdesMealPlan;
